import {
  Fragment,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {useAuth} from '@common/auth/use-auth';
import {NotificationDialogTrigger} from '@common/notifications/dialog/notification-dialog-trigger';
import {Menu, MenuTrigger} from '@ui/menu/menu-trigger';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {createSvgIconFromTree} from '@ui/icons/create-svg-icon';
import {Trans} from '@ui/i18n/trans';
import {IconButton} from '@ui/buttons/icon-button';
import {Item} from '@ui/forms/listbox/item';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import {CustomMenu} from '@common/menus/custom-menu';
import {useSettings} from '@ui/settings/use-settings';
import {ButtonColor} from '@ui/buttons/get-shared-button-style';
import {MenuIcon} from '@ui/icons/material/Menu';
import {
  NavbarAuthUser,
  NavbarAuthUserProps,
} from '@common/ui/navigation/navbar/navbar-auth-user';
import {NavbarAuthButtons} from '@common/ui/navigation/navbar/navbar-auth-buttons';
import {useDarkThemeVariables} from '@ui/themes/use-dark-theme-variables';
import {Logo} from '@common/ui/navigation/navbar/logo';
import {useLightThemeVariables} from '@ui/themes/use-light-theme-variables';
import {isAbsoluteUrl} from '@ui/utils/urls/is-absolute-url';
import {MenuItemConfig} from '@common/menus/menu-config';
import {useIsMobileMediaQuery} from '@ui/utils/hooks/is-mobile-media-query';
import {
  ArrowBackIcon,
  ChevronLeftIcon,
  CloudUploadIcon,
  DashboardIcon,
  DiamondIcon,
  MicIcon,
} from '@ui/icons/material/all-icons';
import {Button} from '@ui/buttons/button';
import {
  isAndroidApp,
  isRenderedAsIOS,
  isiOSApp,
} from '@common/core/platform-detection';
import {useUserRolesAccess} from '@common/auth/user-roles-access';
import {LocaleSwitcher} from '@common/locale-switcher/locale-switcher';
import {Link} from 'react-router-dom';

type NavbarColor =
  | 'primary'
  | 'white'
  | 'bg'
  | 'bg-alt'
  | 'transparent'
  | string;

export interface NavbarProps {
  showBackButton?: boolean | null;
  hideLogo?: boolean | null;
  toggleButton?: ReactElement;
  children?: ReactNode;
  className?: string;
  color?: NavbarColor;
  bgOpacity?: number | string;
  darkModeColor?: NavbarColor;
  logoColor?: 'dark' | 'light';
  textColor?: string;
  primaryButtonColor?: ButtonColor;
  border?: string;
  size?: 'xs' | 'sm' | 'md';
  rightChildren?: ReactNode;
  menuPosition?: string;
  authMenuItems?: NavbarAuthUserProps['items'];
  alwaysDarkMode?: boolean;
  wrapInContainer?: boolean;
  boxed?: boolean;
  fixed?: boolean; // Active/désactive le positionnement fixe
  fixedOnTop?: string; // Classe lorsque la navbar est tout en haut
  fixedOnScroll?: string; // Classe lorsque la page est scrollée
  showNotifButton?: boolean;
  hideOnScroll?: boolean;
}
export function Navbar(props: NavbarProps) {
  let {
    showBackButton,
    hideLogo,
    toggleButton,
    children,
    className,
    border,
    size = 'md',
    color,
    textColor,
    darkModeColor,
    rightChildren,
    menuPosition,
    logoColor,
    primaryButtonColor,
    authMenuItems,
    alwaysDarkMode = false,
    wrapInContainer = false,
    fixed = false,
    fixedOnTop = 'bg-transparent',
    fixedOnScroll = 'bg-primary shadow-2xl',
    showNotifButton = true,
    hideOnScroll = false,
  } = props;
  const navigate = useNavigate();
  const isDarkMode = useIsDarkMode() || alwaysDarkMode;
  const {notifications} = useSettings();
  const {isLoggedIn} = useAuth();
  const darkThemeVars = useDarkThemeVariables();
  const lightThemeVars = useLightThemeVariables();
  const [isScrolled, setIsScrolled] = useState(false);
  const [offset, setOffset] = useState(0);
  const isMobile = useIsMobileMediaQuery();
  const lastRawY = useRef(0);
  const lastY = useRef(0);
  const pendingUp = useRef(0);
  const revealMode = useRef(false);

  showNotifButton = showNotifButton && isLoggedIn && notifications?.integrated;

  color = !fixed
    ? color ?? lightThemeVars?.['--be-navbar-color'] ?? 'primary'
    : undefined;

  darkModeColor = !fixed
    ? darkModeColor ?? darkThemeVars?.['--be-navbar-color'] ?? 'bg-alt'
    : undefined;

  if (isDarkMode) {
    color = darkModeColor;
  }

  // Observer le défilement pour ajuster les classes
  useEffect(() => {
    if (fixed) {
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 10); // Active au-delà de 10px de défilement
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [fixed]);

  useEffect(() => {
    if (!hideOnScroll) {
      setOffset(0);
      lastRawY.current = 0;
      lastY.current = 0;
      pendingUp.current = 0;
      revealMode.current = false;
      return;
    }

    const mainEl = document.querySelector('main');
    if (!mainEl) return;

    const heights = {xs: 48, sm: 54, md: 64};
    const navH = heights[size] ?? heights.md;
    const maxOffset = navH * 2;
    const threshold = 20;

    const onScroll = () => {
      const rawY = mainEl.scrollTop;
      const maxScroll = mainEl.scrollHeight - mainEl.clientHeight;
      const deltaRaw = rawY - lastRawY.current;
      const overscrollTop = rawY <= 0 && deltaRaw < 0;
      const overscrollBottom = rawY >= maxScroll && deltaRaw > 0;

      if (overscrollTop || overscrollBottom) {
        lastRawY.current = rawY;
        return;
      }

      const clampedY = Math.max(0, Math.min(rawY, maxScroll));
      const delta = clampedY - lastY.current;
      const canHide = clampedY >= navH;

      if (delta > 0 && canHide) {
        revealMode.current = false;
        pendingUp.current = 0;
        setOffset(prev => Math.min(prev + delta, maxOffset));
      } else if (delta < 0) {
        pendingUp.current += -delta;

        if (revealMode.current) {
          setOffset(prev => Math.max(prev + delta, 0));
        } else if (pendingUp.current >= threshold) {
          revealMode.current = true;
          setOffset(prev => Math.max(prev + delta, 0));
        }
      }

      lastRawY.current = rawY;
      lastY.current = clampedY;
    };

    mainEl.addEventListener('scroll', onScroll, {passive: true});
    return () => mainEl.removeEventListener('scroll', onScroll);
  }, [hideOnScroll, size]);

  const sizeNavbar =
    isiOSApp() || isAndroidApp()
      ? '' // iOS app uses medium size for navbar
      : size === 'md'
      ? 'h-64 py-8'
      : size === 'sm'
      ? 'h-54 py-4'
      : size === 'xs'
      ? 'h-48 py-4'
      : '';
  const opacity =
    hideOnScroll && size
      ? 1 - offset / (({xs: 48, sm: 54, md: 64}[size] ?? 64) * 2)
      : 1;

  return (
    <div
      style={{
        ...(alwaysDarkMode ? darkThemeVars : undefined),
        transform: hideOnScroll ? `translateY(${-offset}px)` : undefined,
        opacity,
      }}
      className={clsx(
        'transition-all duration-300 ease-in-out', // Animation fluide
        color ? getColorStyle(color, textColor) : null,
        fixed ? 'fixed left-0 top-0 z-[1029] w-full' : '', // Position fixe
        fixed ? (isScrolled ? fixedOnScroll : fixedOnTop) : null, // Gestion des classes dynamiques
        sizeNavbar,
        border,
        !fixed && isMobile ? 'fixed left-0 top-0 z-[1029] w-full' : '',
        className,
      )}
    >
      <div
        className={clsx(
          'flex items-center justify-end gap-10 pl-14 pr-8 md:pl-20 md:pr-20',
          !isiOSApp() && !isAndroidApp() ? 'h-full' : '',
          wrapInContainer && 'container mx-auto',
        )}
      >
        {showBackButton ? (
          <Button onClick={() => navigate(-1)} variant="outline">
            {isRenderedAsIOS() ? <ChevronLeftIcon /> : <ArrowBackIcon />}
            <span className="ml-3 mt-3">
              <Trans message="Go back" />
            </span>
          </Button>
        ) : !hideLogo ? (
          <Logo isDarkMode={isDarkMode} color={color} logoColor={logoColor} />
        ) : null}
        {toggleButton}
        {children}
        <MobileMenu position={menuPosition} />
        <DesktopMenu position={menuPosition} />
        <div className="ml-auto flex items-center gap-4 md:gap-14">
          {rightChildren}
          {isLoggedIn ? (
            <NavbarAuthUser items={authMenuItems} />
          ) : (
            <NavbarAuthButtons
              navbarColor={color}
              primaryButtonColor={primaryButtonColor}
            />
          )}
          {showNotifButton && <NotificationDialogTrigger />}
        </div>
      </div>
    </div>
  );
}

export function NavbarActionButtons() {
  const {player, billing} = useSettings();
  const {isLoggedIn, hasPermission, isSubscribed} = useAuth();
  const {userRoles, userAccess} = useUserRolesAccess();
  const isMobile = useIsMobileMediaQuery();
  const isDarkMode = useIsDarkMode();
  const linkBackstage = '/backstage';
  const linkAdmin = '/admin';
  const linkPricing: string = userRoles.artists
    ? '/pricing/artists'
    : userRoles.creators
    ? '/pricing/creators'
    : userRoles.labels
    ? '/pricing/labels'
    : userRoles.distributors
    ? '/pricing/distributors'
    : userRoles.authors
    ? '/pricing/authors'
    : '/pricing/users';
  const showUploadButton =
    player?.show_upload_btn && isLoggedIn && hasPermission('medias.create');
  const showTryProButton =
    billing?.enable &&
    isLoggedIn &&
    hasPermission('plans.view') &&
    !isSubscribed;
  const showBackstageButton = isLoggedIn && userAccess.backstage.access;
  const showAdminButton = isLoggedIn && userAccess.admin.access;

  const premiumButtonColor = isDarkMode ? 'warning' : 'success';

  /*
{!isMobile 
              ? <Button elementType={Link} to="/" variant="outline" color={"white"} size="xs" startIcon={<PlayCircleIcon />}>
                <Trans message="Player" />
              </Button>
              : <IconButton elementType={Link} to="/" color={"white"} size={"md"}>
                <PlayCircleIcon />
              </IconButton>
            }
  */

  return (
    <Fragment>
      {!isMobile && <LocaleSwitcher buttonSize="xs" buttonVariant="outline" />}
      {showTryProButton && (
        <Fragment>
          {!isMobile ? (
            <Button
              elementType={Link}
              to={linkPricing}
              variant="outline"
              color={premiumButtonColor}
              size="xs"
              startIcon={<DiamondIcon size="sm" />}
            >
              <Trans message="Premium" />
            </Button>
          ) : (
            <IconButton
              elementType={Link}
              to={linkPricing}
              variant="text"
              color={premiumButtonColor}
              size={'md'}
              className="mx-8"
            >
              <DiamondIcon />
            </IconButton>
          )}
        </Fragment>
      )}
      {showUploadButton && (
        <Fragment>
          {!isMobile ? (
            <Button
              elementType={Link}
              to={'/backstage/upload'}
              variant="outline"
              color={'paper'}
              size="xs"
              startIcon={<CloudUploadIcon size="sm" />}
            >
              <Trans message="Upload" />
            </Button>
          ) : (
            <IconButton
              elementType={Link}
              to={'/backstage/upload'}
              variant="text"
              color={'paper'}
              size={'md'}
              className="mx-8"
            >
              <CloudUploadIcon />
            </IconButton>
          )}
        </Fragment>
      )}
      {showBackstageButton && (
        <Fragment>
          {!isMobile ? (
            <Button
              elementType={Link}
              to={linkBackstage}
              variant="outline"
              color={'paper'}
              size="xs"
              startIcon={<MicIcon size="sm" />}
            >
              <Trans message="Backstage" />
            </Button>
          ) : (
            <IconButton
              elementType={Link}
              to={linkBackstage}
              variant="text"
              color={'paper'}
              size={'md'}
              className="mx-8"
            >
              <MicIcon />
            </IconButton>
          )}
        </Fragment>
      )}
      {showAdminButton && (
        <Fragment>
          {!isMobile ? (
            <Button
              elementType={Link}
              to={linkAdmin}
              variant="outline"
              color={'paper'}
              size="xs"
              startIcon={<DashboardIcon size="sm" />}
            >
              <Trans message="Admin area" />
            </Button>
          ) : (
            <IconButton
              elementType={Link}
              to={linkAdmin}
              variant="text"
              color={'paper'}
              size={'md'}
              className="mx-8"
            >
              <DashboardIcon />
            </IconButton>
          )}
        </Fragment>
      )}
    </Fragment>
  );
}

interface DesktopMenuProps {
  position: NavbarProps['menuPosition'];
}
function DesktopMenu({position}: DesktopMenuProps) {
  return (
    <CustomMenu
      className="mx-14 text-sm max-md:hidden"
      itemClassName={isActive =>
        clsx(
          'opacity-90 hover:underline hover:opacity-100',
          isActive && 'opacity-100',
        )
      }
      menu={position}
    />
  );
}

interface MobileMenuProps {
  position: NavbarProps['menuPosition'];
}
function MobileMenu({position}: MobileMenuProps) {
  const navigate = useNavigate();
  const menu = useCustomMenu(position);

  if (!menu?.items.length) {
    return null;
  }

  const handleItemClick = (item: MenuItemConfig) => {
    if (isAbsoluteUrl(item.action)) {
      window.open(item.action, item.target)?.focus();
    } else {
      navigate(item.action);
    }
  };

  return (
    <MenuTrigger>
      <IconButton className="md:hidden" aria-label="Toggle menu">
        <MenuIcon />
      </IconButton>
      <Menu>
        {menu.items.map(item => {
          const Icon = item.icon && createSvgIconFromTree(item.icon);
          return (
            <Item
              value={item.action}
              onSelected={() => handleItemClick(item)}
              key={item.id}
              startIcon={Icon && <Icon />}
            >
              <Trans message={item.label} />
            </Item>
          );
        })}
      </Menu>
    </MenuTrigger>
  );
}

function getColorStyle(color: string, textColor?: string): string {
  switch (color) {
    case 'primary':
      return `bg-primary ${textColor || 'text-on-primary'} md:border-b-primary`;
    case 'white':
      return `bg ${textColor || 'text-main'}`;
    case 'bg':
      return `bg ${textColor || 'text-main'} md:border-b`;
    case 'bg-alt':
      return `bg-alt ${textColor || 'text-main'} md:border-b`;
    case 'transparent':
      return `bg-transparent ${textColor || 'text-white'}`;
    default:
      return `${color} ${textColor}`;
  }
}
